import { Carousel, Fancybox } from "@fancyapps/ui";
import { Autoplay } from "@fancyapps/ui/dist/carousel.autoplay.esm.js";
Carousel.Plugins.Autoplay = Autoplay;

const sliders = document.querySelectorAll("[data-slider]");
let sliderInstancesMap = new Map();
let focusSelectedSlideButton = false;
let resizeTimeout;

Fancybox.bind("[data-fancybox=\"video-gallery\"]", {
  closeButton: true,
});

Fancybox.bind("[data-fancybox=\"gallery\"]", {
  Toolbar: {
    display: ["counter", "slideshow", "fullscreen", "download", "close"],
  },
});

function getElementWidthPx(el) {
  const widthStr = getComputedStyle(el).width;

  if (widthStr.endsWith('%')) {
    const percent = parseFloat(widthStr);
    const parentWidth = el.parentElement?.offsetWidth || 0;
    return parentWidth * percent / 100;
  }

  return parseFloat(widthStr);
}

function getSlidesPerPage(sliderContainer, tolerance = 5) {
  const viewport = sliderContainer.querySelector('.carousel__viewport');
  const slides = sliderContainer.querySelectorAll('.carousel__slide');
  if (!slides.length) return 1;

  const viewportWidth = viewport?.clientWidth || sliderContainer.clientWidth || 0;
  if (!viewportWidth) return 1;

  let count = 0;
  let accumulatedWidth = 0;

  for (const slide of slides) {
    const style = window.getComputedStyle(slide);
    const marginLeft = parseFloat(style.marginLeft) || 0;
    const marginRight = parseFloat(style.marginRight) || 0;
    const totalWidth = getElementWidthPx(slide) + marginLeft + marginRight;
    accumulatedWidth += totalWidth;
    if (accumulatedWidth <= viewportWidth + tolerance) count++;
    else break;
  }

  return count || 1;
}

export function initializeSlider(slider) {
  const existing = sliderInstancesMap.get(slider);

  if (existing) {
    existing.destroy();
    sliderInstancesMap.delete(slider);
  }

  if (!slider.querySelector(".liveregion")) {
    let liveregion = document.createElement("div");
    liveregion.setAttribute("aria-live", "polite");
    liveregion.setAttribute("aria-atomic", "true");
    liveregion.setAttribute("class", "liveregion d-visually-hidden");
    slider.appendChild(liveregion);
  }

  const disableDots = slider.hasAttribute("data-slider-no-dots");
  const isInfinite = slider.hasAttribute("data-slider-no-infinite");
  const enableAutoplay = slider.hasAttribute("data-slider-autoplay");
  const doubleSlider = slider.hasAttribute("data-slider-double");
  const disableFill = slider.hasAttribute("data-slider-no-fill");

  const slidesPerPage = Math.max(1, getSlidesPerPage(slider));

  const options = {
    Dots: !disableDots,
    center: false,
    slidesPerPage: slidesPerPage,
    friction: 0.85,
    infinite: !isInfinite,
    fill: !disableFill,
    on: {
      change: (sliderInstance) => {
        setTimeout(() => {
          onPageChange(slider, sliderInstance);
        }, 66);
      },
    },
  };

  if (enableAutoplay) {
    options.Autoplay = {
      timeout: 5000,
    };
  } else {
    options.Autoplay = false;
  }

  let sliderInstance;

  if (doubleSlider) {
    const doubleSliderWrapper = slider.closest("[data-slider-double-wrapper]");
    const mainCarousel = new Carousel(slider, options);
    sliderInstance = new Carousel(doubleSliderWrapper.querySelector("[data-slider-double-nav]"), {
      Sync: {
        target: mainCarousel,
      },
      Dots: false,
      Navigation: false,
      friction: 0,
      Autoplay: false
    });
  } else {
    sliderInstance = new Carousel(slider, options);
  }

  const originalSlideTo = sliderInstance.slideTo;

  sliderInstance.slideTo = function (...args) {
    const focused = document.activeElement;
    sliderInstance.pages[sliderInstance.page].slides.forEach((slide) => {
      if (slide.$el.contains(focused)) focused.blur();
    });
    return originalSlideTo.apply(this, args);
  }

  sliderInstance._radioNavigationByArrow = false;

  sliderInstancesMap.set(slider, sliderInstance);
  makeSelectedSlidesFocusable(slider);
  editSliderButtons(slider);
  handleSliderArrowKeyDown(slider, sliderInstance);
  initDotsKeyboardNavigation(slider, sliderInstance);

  onPageChange(slider, sliderInstance);
}

function onPageChange(slider, sliderInstance) {
  slider.querySelector(".liveregion").textContent = "Слайд " + (sliderInstance.page + 1) + " з " + sliderInstance.pages.length;
  updateDots(slider);
  makeSelectedSlidesFocusable(slider);

  if (sliderInstance._radioNavigationByArrow) {
    sliderInstance._radioNavigationByArrow = false;
    return;
  }

  const currentSlides = sliderInstance.pages[sliderInstance.page].slides;
  const firstRadio = currentSlides?.map(s => s.$el.querySelector('input[type="radio"]')).find(r => r != null);

  if (firstRadio) checkRadio(firstRadio);
}

function checkRadio(radio) {
  radio.checked = true;
  radio.dispatchEvent(new Event("change"));
}

function navigateDotsByArrowKeys(e, dotsContainer) {
  const isArrowLeft = e.key === "ArrowLeft";
  let newDot = isArrowLeft ? e.target.previousElementSibling : e.target.nextElementSibling;
  if (!newDot) newDot = isArrowLeft ? dotsContainer.lastElementChild : dotsContainer.firstChild;
  newDot.click();
  setTimeout(() => {
    newDot.focus();
  }, 66);
}

function initDotsKeyboardNavigation(slider) {
  const dotsContainer = slider.querySelector(".carousel__dots");
  if (!dotsContainer) return;

  slider.querySelectorAll(".carousel__dot").forEach((dot, index) => {
    dot.setAttribute("title", `Перейти на слайд №${index + 1}`);

    dot.addEventListener("keydown", (e) => {
      switch (e.key) {
        case " ":
          e.preventDefault();
          e.target.click();
          break;
        case "ArrowLeft":
        case "ArrowRight":
          e.preventDefault();
          navigateDotsByArrowKeys(e, dotsContainer);
          break;
      }
    });
  });
}

function updateDots(slider) {
  const dotsContainer = slider.querySelector(".carousel__dots");
  if (dotsContainer) {
    const selectedDot = slider.querySelector(".carousel__dot.is-selected");

    slider.querySelectorAll(".carousel__dot").forEach((dot) => {
      dot.classList.remove(
        "is-next",
        "is-after-next",
        "is-prev",
        "is-before-prev",
      );
    });

    const prev = selectedDot.previousElementSibling;
    if (prev) {
      prev.classList.add("is-prev");
      if (prev.previousElementSibling) {
        prev.previousElementSibling.classList.add("is-before-prev");
      }
    }

    const next = selectedDot.nextElementSibling;
    if (next) {
      next.classList.add("is-next");
      if (next.nextElementSibling) {
        next.nextElementSibling.classList.add("is-after-next");
      }
    }
  }
}

function editSliderButtons(slider) {
  const carouselButtons = slider.querySelectorAll(".carousel__button");
  carouselButtons.forEach((button) => {
    const isNext = button.classList.contains('is-next');

    const buttonTitle = isNext ? "Наступний слайд" : "Попередній слайд";
    button.setAttribute("title", buttonTitle);
    button.setAttribute("aria-label", buttonTitle);

    button.addEventListener("keydown", (e) => {
      if (e.key === 'Enter' || e.key === ' ') focusSelectedSlideButton = false;
    });
  });
}

function handleSliderArrowKeyDown(slider, sliderInstance) {
  if (slider.__keyHandler) slider.removeEventListener("keydown", slider.__keyHandler);

  function checkAndFocus(radio) {
    checkRadio(radio);
    radio.focus();
  }

  function handleStandardNav(e) {
    switch (e.key) {
      case "ArrowRight":
        focusSelectedSlideButton = true;
        sliderInstance.slideNext();
        break;
      case "ArrowLeft":
        focusSelectedSlideButton = true;
        sliderInstance.slidePrev();
        break;
      case " ":
      case "Enter":
        if (e.target.matches('a.carousel__slide')) {
          e.preventDefault();
          e.target.click();
        }
        break;
    }
  }

  function switchRadioPage(direction) {
    const isNext = direction === 1;
    const { page, pages } = sliderInstance;
    const isBoundary = isNext ? page === pages.length - 1 : page === 0;

    if (isBoundary) return;

    sliderInstance._radioNavigationByArrow = true;
    isNext ? sliderInstance.slideNext() : sliderInstance.slidePrev();

    setTimeout(() => {
      const newPageIdx = sliderInstance.page;
      const newPageSlides = sliderInstance.pages[newPageIdx].slides;
      
      const targetSlide = isNext ? newPageSlides[0] : newPageSlides[newPageSlides.length - 1];
      const radio = targetSlide.$el.querySelector('input[type="radio"]');
      
      checkAndFocus(radio);
    }, 0);
  }

  function handleRadioNav(e) {
    let direction;

    switch (e.key) {
      case "ArrowLeft":
      case "ArrowUp":
        direction = -1;
        break;
      case "ArrowRight":
      case "ArrowDown":
        direction = 1;
        break;
      default:
        return;
    }

    const currentPage = sliderInstance.pages[sliderInstance.page];
    const radios = currentPage.slides.map(s => s.$el.querySelector('input[type="radio"]'));
    const currentIndex = radios.indexOf(e.target);

    if (currentIndex === -1) return;

    const nextIndex = currentIndex + direction;

    e.preventDefault();
    if (nextIndex >= 0 && nextIndex < radios.length)
      checkAndFocus(radios[nextIndex]);
    else
      switchRadioPage(direction);
  }

  const handler = (e) => {
    if (e.target.matches('.carousel__slide button, .carousel__slide a, a.carousel__slide'))
      handleStandardNav(e);
    else if (e.target.matches('.carousel__slide input[type="radio"]'))
      handleRadioNav(e);
  };

  slider.__keyHandler = handler;
  slider.addEventListener("keydown", handler);
}

function makeSelectedSlidesFocusable(slider) {
  const prevSlideButtons = slider.querySelectorAll('.carousel__slide[aria-hidden="true"] button, .carousel__slide[aria-hidden="true"] a, a.carousel__slide[aria-hidden="true"], .carousel__slide iframe');
  prevSlideButtons?.forEach((button) => {
    button.setAttribute("tabindex", -1);
  });

  const selectedSlideButtons = slider.querySelectorAll('.is-selected button, .is-selected a, a.is-selected, .is-selected iframe');
  selectedSlideButtons?.forEach((button, index) => {
    if (!index && focusSelectedSlideButton) button.focus();
    button.setAttribute("tabindex", 0);
  });
}

function resizeThrottler() {
  if (!resizeTimeout) {
    resizeTimeout = setTimeout(function() {
      resizeTimeout = null;
      sliders.forEach((slider) => {
        const sliderInstance = sliderInstancesMap.get(slider);
        if (!sliderInstance) return;
        let currentSlidesPerPage = sliderInstance.pages[0].slides.length;
        if (currentSlidesPerPage !== getSlidesPerPage(slider)) initializeSlider(slider);
      });
    }, 66);
  }
}

window.addEventListener('resize', resizeThrottler, false);

window.addEventListener('DOMContentLoaded', () => {
  sliders.forEach((slider) => {
    initializeSlider(slider);
  });
});
